import { useQuery, useSubscription } from '@apollo/client';
import { get, set } from 'lodash/fp';
import pick from 'lodash/fp/pick';
import identity from 'lodash/identity';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Route } from 'react-router';
import { Switch, withRouter } from 'react-router-dom';
import { compose, withProps } from 'recompose';
import { ThemeProvider } from 'styled-components';
import { withScope } from '../../../contexts/ScopeContext';
import scheme, { listActiveAndPastPatients } from '../../../graph/patients';
import { listPhysicianPatientsActiveAndPast } from '../../../graph/patientChart';
import { procedureSteps } from '../../../graph/procedureSteps';
import { useScope } from '../../../hooks/useScope';
import activeAndPastProceduresSource from '../../../omnisearch/sources/hospital/activeAndPastProcedures';
import activeAndPastProceduresWithPhysicianSource from '../../../omnisearch/sources/hospital/activeAndPastProceduresWithPhysician';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import { SessionContext, withSession } from '../../../state/Session';
import { PatientType } from '../../../types/Patient';
import UserRoles from '../../../types/UserRoles';
import { NamedRange } from '../../core/DatePicker';
import PatientInputFactory from '../../inputs/patient/PatientInput';
import Filters from '../../pages/analytics/Filters';
import { chartingStore } from '../../pages/kiosk/charting/modules/chartingStore';
import { useChartingSession } from '../../pages/kiosk/charting/modules/hooks';
import useTabs from '../../pages/kiosk/charting/modules/useTabs';
import PatientShowActions from '../../patient/PatientShowActions';
import PatientShowTitle from '../../patient/PatientShowTitle';
import { ANESTHESIOLOGIST, PHYSICIAN } from '../role/enums';
import ActiveAndPastPatientCharting from './ActiveAndPastPatientCharting';
import ActiveAndPastPatientsDictation from './ActiveAndPastPatientsDictation';
import {
  braceletIdColumn,
  dateOfVisit,
  idColumn,
  nameColumn,
  note,
  operationRoom,
  patientAgeColumn,
  patientSexColumn,
  phoneNumberColumnWithHistory,
  physicianColumn,
  preOpBedNumber,
  procedureDurationColumn,
  procedureHstIdColumn,
  procedureStartTimeColumn,
  procedureTypeColumn,
  providerIdColumn,
  recoveryBedNumber,
  roomOrDateOfVisit,
  smsNotificationNumbersColumn,
  visitDurationColumn,
} from './columns';
import { mapEditItemProps, mapListProps, prepareCreateData } from './transducers';
import { baseEventColumns, getEventsColumns } from './util/patientEvents';
import PatientListView from './views/PatientListView';
import PatientMobileListItem from './views/PatientMobileListItem';
import PatientView from './views/PatientView';

const PatientCreationInput = PatientInputFactory(true, false);
const PatientUpdateInput = PatientInputFactory(false);
const Empty = { illustration: theme => theme.illustrations.patients };

const filterEventColumns = props => {
  const hasPostop = get('scope.hospital.modules.hasPostop')(props);
  return hasPostop
    ? props
    : set(
        'columns',
        get('columns')(props).filter(column => column.title !== 'Post Op Entry')
      )(props);
};

export const CustomFilters = props => <Filters {...props} />;

const emptyArray = [];

// Wrapper component to handle patient subscription
const PatientSubscriptionWrapper = ({ idProvider, physicianIds, children, ...props }) => {
  const patientId = parseInt(idProvider(props), 10);

  const subscriptionQuery = physicianIds?.length > 0 ? scheme.subscriptionWithPhysician : scheme.subscription;
  const variables = physicianIds?.length > 0 ? { id: patientId, physicianIds } : { id: patientId };

  const { data, loading, error } = useSubscription(subscriptionQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  if (loading) return null;
  if (error) {
    console.error('Patient subscription error:', error);
    return null;
  }

  return children({ data, loading, error, idProvider, ...props });
};

const ActiveAndPastPatients = withRouter(props => {
  const { data } = useQuery(procedureSteps);

  const sessionContext = useContext(SessionContext);
  const roles = sessionContext.session?.user?.metadata ?? [];
  const isMonitoringEngineer = roles.indexOf(UserRoles.MonitoringEngineer) >= 0;

  const isOrTabletV2 = props?.scope?.hospital?.modules?.orTabletV2 ?? true;
  const steps = data?.procedureSteps || [];

  const eventColumns = isOrTabletV2 ? getEventsColumns(steps) : baseEventColumns;

  const tabs = useTabs();

  const scope = useScope();

  const chartingSession = useChartingSession();

  const organizationId = get('hospital.id')(scope);
  const name = get('session.user.name')(props);
  const userMetadata = get('session.user.metadata', props) || emptyArray;
  const memberships = get('session.user.memberships', props) || emptyArray;
  const membership = memberships.find(m => m.organization.id === organizationId);
  const membershipMetadata = get('metadata', membership) || emptyArray;
  const metadata = useMemo(() => [...userMetadata, ...membershipMetadata], [membershipMetadata, userMetadata]);

  const isPhysician = metadata.indexOf(PHYSICIAN) >= 0;
  const isAnesthesiologist = metadata.indexOf(ANESTHESIOLOGIST) >= 0;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    chartingStore.setState({
      session: {
        id: -1,
        name,
        pin: '-1',
        role: isPhysician ? 'physician' : isAnesthesiologist ? 'anesthesiologist' : 'staff',
      },
      pinTimeout: null,
      keepAlive: 0,
    });
    setReady(true);
  }, [name, isPhysician, isAnesthesiologist]);

  const physicianIdsFromMetadata = useMemo(
    () =>
      metadata
        .filter(m => m.startsWith('Physician('))
        .map(physicianMetadata => {
          const match = physicianMetadata.match(/Physician\((\d+)\)/);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter(id => id !== null),
    [metadata]
  );

  const hasPhysicianIds = physicianIdsFromMetadata?.length > 0;

  const PatientCRUDComponent = GraphQLCRUD({
    entityName: 'Patient',
    scheme: {
      ...scheme,
      list: hasPhysicianIds ? listPhysicianPatientsActiveAndPast(physicianIdsFromMetadata) : listActiveAndPastPatients,
    },
    List: {
      tableKey: 'ActiveAndPastPatients',
      MobileItemComponent: PatientMobileListItem,
      useColumnSelection: true,
      useCSVExport: !isMonitoringEngineer,
      columns: [
        idColumn,
        providerIdColumn,
        procedureHstIdColumn,
        braceletIdColumn,
        nameColumn('id'),
        patientAgeColumn,
        patientSexColumn,
        procedureStartTimeColumn,
        procedureDurationColumn,
        physicianColumn,
        procedureTypeColumn,
        operationRoom,
        preOpBedNumber,
        recoveryBedNumber,
        visitDurationColumn,
        roomOrDateOfVisit,
        ...eventColumns,
      ],
      View: PatientListView,
      FilterComponent: withProps({
        textSearchKey: 'name',
        hideSpecialitySelectInput: hasPhysicianIds,
        hideProcedureTypeSelectInput: true,
        hideMobile: true,
        hidePhysicianSelectInput: hasPhysicianIds,
        physicianIds: physicianIdsFromMetadata,
        reload: false, // Disable page reload on filter changes
        dontReloadOn: ['name', 'physician', 'procedureType', 'speciality', 'status', 'dateRange'], // Never reload on these filters
      })(CustomFilters),
      defaultFilterValues: {
        dateRange: NamedRange.lastSevenDays(),
      },
      pickFilter: pick([
        'name',
        'physician',
        'procedureType',
        'hospital',
        'status',
        'dateRange',
        'category',
        'speciality',
      ]),
      Empty,
      mapListProps: compose(mapListProps, filterEventColumns),
      containsSeparator: true,
    },
    Show: {
      columns: [
        idColumn,
        providerIdColumn,
        physicianColumn,
        procedureTypeColumn,
        smsNotificationNumbersColumn,
        phoneNumberColumnWithHistory,
        dateOfVisit,
        note,
      ],
      Empty,
      View: PatientView,
      Title: PatientShowTitle,
      Actions: PatientShowActions,
      itemProvider: Component => props => (
        <PatientSubscriptionWrapper {...props} physicianIds={hasPhysicianIds ? physicianIdsFromMetadata : null}>
          {subscriptionProps => <Component {...subscriptionProps} />}
        </PatientSubscriptionWrapper>
      ),
    },
    ...(!isPhysician && {
      Create: {
        Input: PatientCreationInput,
        Empty,
        prepareCreateData,
        initialValue: { type: PatientType.Out },
      },
    }),
    ...(!isPhysician && {
      Edit: {
        mapEditItemProps,
        Input: PatientUpdateInput,
        Empty,
        prepareUpdateData: prepareCreateData,
      },
    }),
    searchSource: hasPhysicianIds ? activeAndPastProceduresWithPhysicianSource : activeAndPastProceduresSource,
  });

  return (
    <ThemeProvider theme={{ gridListColumnsTemplate: 'auto auto auto minmax(10vw,2fr) auto' }}>
      {ready &&
        (Array.isArray(metadata) ? (
          <Switch>
            {tabs.map(tab => (
              <Route
                key={tab.path}
                {...tab}
                path={`${props.match.url}/:id${tab.path}`}
                component={undefined}
                render={props => {
                  const patientId = parseInt(get('params.id')(props.match), 10);

                  return (
                    <>
                      <ActiveAndPastPatientCharting
                        {...props}
                        hospitalId={organizationId}
                        patientId={patientId}
                        questionnaireType={tab.chartType}
                        room="App"
                      />
                      <Route
                        path={`${props.match.path}/dictation`}
                        render={props => (
                          <ActiveAndPastPatientsDictation
                            {...props}
                            hospitalId={organizationId}
                            questionnaireType={tab.chartType}
                          />
                        )}
                      />
                    </>
                  );
                }}
              />
            ))}
            <Route render={() => <PatientCRUDComponent {...props} />} />
          </Switch>
        ) : (
          <PatientCRUDComponent {...props} />
        ))}
    </ThemeProvider>
  );
});

export default compose(withSession(identity), withScope)(ActiveAndPastPatients);
